import {useCallback, useContext, useEffect, useState} from 'react';
import Section from '../UI/Section';
import {Context} from '../../context';
import LoadFundsUI from './LoadFundsUI';
import {Switch} from '@mui/material';
import Button from '../UI/Button';
import {cardPaymentMethodExists} from '../../helperFunctions';

const AutoLoad = () => {
  const {
    localeText,
    account,
    loading,
    createAutoLoadProfile,
    disableAutoLoadTransitAccount
  } = useContext(Context);

  const DEFAULT_LOAD_OPTIONS = [5, 10, 20, 50];
  const DEFAULT_AMOUNT_TO_LOAD = 5;
  const DEFAULT_RELOAD_THRESHOLD = 5;
  const LOAD_MIN = '5.00';
  const LOAD_MAX = '100.00';

  const [amountToLoad, setAmountToLoad] = useState(null);
  const [reloadThreshold, setReloadThreshold] = useState(null);
  const [selectedBillingProfileId, setSelectedBillingProfileId] =
    useState(null);
  const [showAutoLoad, setShowAutoLoad] = useState(false);
  const [transitAccountId, setTransitAccountId] = useState(null);
  const [saveButtonActive, setSaveButtonActive] = useState(false);

  const getAutoLoadProfiles = useCallback(() => {
    const allAutoLoadProfiles =
      account.autoPurchaseProfiles?.filter(
        p => p.__typename === 'AutoLoadProfile'
      ) || [];
    const lastInvalidProfile = allAutoLoadProfiles
      .filter(p => p.cancelledDate)
      .pop();
    const lastValidProfile = allAutoLoadProfiles
      .filter(p => !p.cancelledDate)
      .pop();

    return {lastInvalidProfile, lastValidProfile};
  }, [account]);

  const setAutoLoadDefaults = useCallback(() => {
    const {lastInvalidProfile, lastValidProfile} = getAutoLoadProfiles();

    setAmountToLoad(
      lastValidProfile
        ? lastValidProfile.storedValueLoadAmount / 100
        : lastInvalidProfile
        ? lastInvalidProfile.storedValueLoadAmount / 100
        : DEFAULT_AMOUNT_TO_LOAD
    );

    setReloadThreshold(
      lastValidProfile
        ? lastValidProfile.storedValueThreshold / 100
        : lastInvalidProfile
        ? lastInvalidProfile.storedValueThreshold / 100
        : DEFAULT_RELOAD_THRESHOLD
    );

    setSelectedBillingProfileId(
      lastValidProfile
        ? lastValidProfile.billingProfileId
        : lastInvalidProfile
        ? lastInvalidProfile.billingProfileId
        : account.paymentMethods[0].id
    );
  }, [getAutoLoadProfiles]);

  useEffect(() => {
    setTransitAccountId(account?.transitAccounts[0]?.id);
  }, [account]);

  // on page load, populate auto load section if profile exists
  useEffect(() => {
    if (!cardPaymentMethodExists(account)) {
      setShowAutoLoad(false);
      return;
    }

    setAutoLoadDefaults();

    setShowAutoLoad(getAutoLoadProfiles()?.lastValidProfile ? true : false);
  }, [account]);

  const toggleAutoLoad = useCallback(
    async e => {
      const switchChecked = e.target.checked;
      if (!cardPaymentMethodExists(account)) {
        setShowAutoLoad(false);
        return;
      }

      // disable auto load profile if exists while toggler unchecked
      if (
        !switchChecked &&
        getAutoLoadProfiles()?.lastValidProfile &&
        transitAccountId
      ) {
        disableAutoLoadTransitAccount(transitAccountId);
      }

      setShowAutoLoad(switchChecked);
    },
    [
      account,
      transitAccountId,
      disableAutoLoadTransitAccount,
      getAutoLoadProfiles
    ]
  );

  const saveAutoLoadSettings = async () => {
    // if load values are outside allowable range, display alert and return
    const outsideLoadTriggerRange =
      +reloadThreshold < +LOAD_MIN || +reloadThreshold > +LOAD_MAX;
    const outsideLoadRange =
      +amountToLoad < +LOAD_MIN || +amountToLoad > +LOAD_MAX;

    if (outsideLoadTriggerRange || outsideLoadRange) {
      let load_errors = [];

      if (outsideLoadTriggerRange) {
        load_errors.push(
          `Minimum Account Balance value must be between $${LOAD_MIN} and $${LOAD_MAX}`
        );
      }
      if (outsideLoadRange) {
        load_errors.push(
          `Auto Load Amount must be between $${LOAD_MIN} and $${LOAD_MAX}`
        );
      }

      alert(load_errors.join('\n'));
      return;
    }

    // exit if the same auto load profile already exists
    if (submissionSettingsSameAsAutoLoadProfile()) {
      return;
    }

    if (getAutoLoadProfiles()?.lastValidProfile) {
      await disableAutoLoadTransitAccount(transitAccountId, {
        reloadAccount: false
      });
    }

    await createAutoLoadProfile({
      billingProfileId: selectedBillingProfileId,
      transitAccountId,
      amountToLoad: Math.round(amountToLoad * 100),
      reloadThreshold: Math.round(reloadThreshold * 100)
    });
  };

  const submissionSettingsSameAsAutoLoadProfile = useCallback(() => {
    const {lastValidProfile} = getAutoLoadProfiles();
    if (!lastValidProfile) {
      return false;
    }
    const {storedValueLoadAmount, storedValueThreshold, billingProfileId} =
      lastValidProfile;

    return (
      Math.round(amountToLoad * 100) === storedValueLoadAmount &&
      Math.round(reloadThreshold * 100) === storedValueThreshold &&
      selectedBillingProfileId === billingProfileId
    );
  }, [
    amountToLoad,
    reloadThreshold,
    selectedBillingProfileId,
    getAutoLoadProfiles
  ]);

  useEffect(() => {
    if (submissionSettingsSameAsAutoLoadProfile()) {
      return setSaveButtonActive(false);
    }
    setSaveButtonActive(true);
  }, [submissionSettingsSameAsAutoLoadProfile]);

  return (
    <Section
      sectionId='section-auto-load'
      heading={localeText('rw_loadfunds_header_auto_load', 'Auto Load')}
      className='row-start-auto col-start-1 lg:row-start-2 lg:col-start-2 flex flex-col gap-2'
    >
      <div className={`${loading && 'opacity-50 pointer-events-none'}`}>
        <div className={`flex items-center`}>
          <Switch
            name='autoPurchaseProfile'
            id='auto-load-toggler'
            checked={showAutoLoad}
            onChange={event => toggleAutoLoad(event)}
          />
          <div className='p-2'>
            <b>{localeText('rw_loadfunds_text_auto_load', 'Auto Load')}</b>

            {showAutoLoad && (
              <select
                id='auto-load-select'
                className='mx-4 p-4 border rounded-md'
                defaultValue={selectedBillingProfileId}
                onChange={e => setSelectedBillingProfileId(e.target.value)}
              >
                {account.paymentMethods
                  .filter(pm => pm.processor !== 'STORED_VALUE')
                  .map(pm => (
                    <option key={pm.id} value={pm.id}>
                      {pm.nickname} ****{pm.accountNumber}
                    </option>
                  ))}
              </select>
            )}
          </div>
        </div>
        {showAutoLoad && (
          <>
            <div className='font-bold p-2'>
              <LoadFundsUI
                title={localeText(
                  'rw_loadfunds_title_minimum_account_balance',
                  'Minimum Account Balance'
                )}
                inputValue={reloadThreshold}
                setInputValue={setReloadThreshold}
                loadOptions={DEFAULT_LOAD_OPTIONS}
              >
                {localeText(
                  'rw_loadfunds_title_minimum_account_balance',
                  'Minimum Account Balance'
                )}
              </LoadFundsUI>
            </div>
            <hr className='m-2 border-gray-400'></hr>
            <div className='font-bold p-2'>
              <LoadFundsUI
                title={localeText(
                  'rw_loadfunds_title_auto_load_amount',
                  'Auto Load Amount'
                )}
                inputValue={amountToLoad}
                setInputValue={setAmountToLoad}
                loadOptions={DEFAULT_LOAD_OPTIONS}
              >
                {localeText(
                  'rw_loadfunds_title_auto_load_amount',
                  'Auto Load Amount'
                )}
              </LoadFundsUI>
            </div>
            <div className='p-2'>
              <Button
                onClick={saveAutoLoadSettings}
                disabled={!saveButtonActive}
              >
                {saveButtonActive
                  ? localeText(
                      'rw_loadfunds_button_save',
                      'Save Auto Load Settings'
                    )
                  : localeText(
                      'rw_loadfunds_button_saved',
                      'Current Settings Saved'
                    )}
              </Button>
            </div>
          </>
        )}
      </div>
    </Section>
  );
};

export default AutoLoad;
